import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDialog } from '@/hooks/useDialog';
import { ViewerIconButton } from './ViewerIconButton';

export interface LightboxItem {
  id: string;
  alt: string;
  /** Compressed image, shown immediately while the full-quality one downloads. */
  src: string;
  /** Full-quality version. Only fetched while this image is open in the viewer. */
  hqSrc?: string;
  /** Intrinsic size; lets the viewer reserve the right box and scale the image to fit the screen. */
  width?: number;
  height?: number;
  /** Blurred placeholder (data URI) behind the image. */
  lqip?: string | null;
}

interface ImageLightboxProps {
  items: LightboxItem[];
  index: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

/** Share of the viewport height the image may use; the rest is left for the controls. */
const MAX_HEIGHT_VH = 88;
const MAX_WIDTH_VW = 95;

/**
 * One image: the compressed version appears instantly, then the full-quality version
 * fades in on top once it has downloaded. Keyed by item id so state resets per image.
 */
const Slide = ({ item }: { item: LightboxItem }) => {
  const [hqReady, setHqReady] = useState(false);
  const [hqFailed, setHqFailed] = useState(false);

  const hasHq = Boolean(item.hqSrc) && item.hqSrc !== item.src;
  const hasSize = Boolean(item.width && item.height);

  // With known dimensions, size the box so the image scales UP or down to fit the screen
  // (a bare <img> would never grow past its file size, hiding the extra quality).
  const boxStyle: React.CSSProperties = {
    ...(hasSize
      ? {
          aspectRatio: `${item.width} / ${item.height}`,
          width: `min(${MAX_WIDTH_VW}vw, calc(${MAX_HEIGHT_VH}vh * ${item.width! / item.height!}))`,
        }
      : undefined),
    ...(item.lqip ? { backgroundImage: `url(${item.lqip})`, backgroundSize: 'cover' } : undefined),
  };
  // Tailwind only emits classes it can read as complete strings, so keep these literal
  // (and in sync with MAX_HEIGHT_VH / MAX_WIDTH_VW above).
  const baseClass = hasSize
    ? 'absolute inset-0 h-full w-full object-contain'
    : 'object-contain max-h-[88vh] max-w-[95vw]';

  return (
    <div className="relative" style={boxStyle}>
      <img src={item.src} alt={item.alt} className={baseClass} draggable={false} />

      {hasHq && !hqFailed && (
        <img
          src={item.hqSrc}
          alt=""
          aria-hidden="true"
          draggable={false}
          onLoad={() => setHqReady(true)}
          onError={() => setHqFailed(true)}
          className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-500 motion-reduce:transition-none ${
            hqReady ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}

      {hasHq && !hqReady && !hqFailed && (
        <span
          role="status"
          className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white"
        >
          Loading full quality…
        </span>
      )}
    </div>
  );
};

/**
 * Full-screen image viewer. Accessible dialog: focus moves in and is restored on close,
 * Tab is trapped, Esc closes, and arrow keys move between images. (Dialog behaviour
 * lives in `useDialog`, shared with the video player.)
 */
const ImageLightbox = ({ items, index, onClose, onIndexChange }: ImageLightboxProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const item = items[index];
  const hasMultiple = items.length > 1;

  const go = useCallback(
    (delta: number) => onIndexChange((index + delta + items.length) % items.length),
    [index, items.length, onIndexChange]
  );

  // Scroll lock, focus handling, Tab trap and Escape are shared with the video player.
  useDialog(dialogRef, { onClose, initialFocusRef: closeButtonRef });

  useEffect(() => {
    if (!hasMultiple) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') go(1);
      else if (event.key === 'ArrowLeft') go(-1);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [go, hasMultiple]);

  // Warm the cache with the neighbours' (small) compressed versions so paging feels instant.
  useEffect(() => {
    if (!hasMultiple) return;
    [-1, 1].forEach((delta) => {
      const neighbour = items[(index + delta + items.length) % items.length];
      new Image().src = neighbour.src;
    });
  }, [hasMultiple, index, items]);

  if (!item) return null;

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/90"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <ViewerIconButton
        ref={closeButtonRef}
        label="Close image viewer"
        path="M6 6l12 12M18 6L6 18"
        onClick={onClose}
        className="absolute right-4 top-4"
      />

      {hasMultiple && (
        <>
          <ViewerIconButton
            label="Previous image"
            path="M15 5l-7 7 7 7"
            onClick={() => go(-1)}
            className="absolute left-3 top-1/2 -translate-y-1/2"
          />
          <ViewerIconButton
            label="Next image"
            path="M9 5l7 7-7 7"
            onClick={() => go(1)}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          />
          <p aria-live="polite" className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/80">
            {index + 1} / {items.length}
          </p>
        </>
      )}

      <Slide key={item.id} item={item} />
    </div>,
    document.body
  );
};

export default ImageLightbox;
